import { type GuidanceSession } from '@fruitful/types';
import { type WorkflowRun } from '@mastra/core/storage';
import {
    type WorkflowRunState,
    type WorkflowState,
} from '@mastra/core/workflows';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useCallback, useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CaptionsIcon,
    CheckboxWithLabel,
    CheckIcon,
    ExternalLinkIcon,
    H6Emphasized,
    IconButton,
    LayersIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    UrlLinkRenderer,
    ViewItemRenderer,
} from '../../common/components/GridCellRenderers';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import {
    adminUserAtom,
    selectedWorkflowRunAtom,
} from '../../entities/entityAtoms';
import { type MembershipDetails } from '../../entities/entityTypes';
import { useGetWorkflowRuns } from '../../llm-service/hooks/useGetWorkflowRuns';
import { useMastraClient } from '../../llm-service/providers/MastraClient';
import { WorkflowId } from '../../llm-service/types/workflowIds';
import { captureException } from '../../logger/Logger';
import PostSessionWorkflowRunOutputModal from '../../workflows/components/PostSessionWorkflowRunOutputModal';
import {
    getSessionIdFromWorkflowRun,
    normalizeWorkflowRunStatus,
    WorkflowStatus,
} from '../../workflows/utils/postSessionWorkflowUtils';
import { useReplayPostSessionWorkflow } from '../hooks/useReplayPostSessionWorkflow';
import { canReplayStoredTranscript } from '../utils/canReplayStoredTranscript';
import { getMembershipGuidanceSessionDisplayName } from '../utils/membershipGuidanceSessionDisplayName';
import { CreateGuidanceSessionModal } from './CreateGuidanceSessionModal';
import { GuidanceSessionNotesModal } from './GuidanceSessionNotesModal';
import { guidanceSessionStatusFromSession } from './GuidanceSessionStatusCell';
import { GuidanceSessionTranscriptModal } from './GuidanceSessionTranscriptModal';

export const GuidanceSessionsTable = ({
    guidanceSessions,
    membershipDetails,
}: {
    guidanceSessions: GuidanceSession[];
    membershipDetails: MembershipDetails;
}) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const adminUser = useAtomValue(adminUserAtom);
    const setSelectedWorkflowRunAtom = useSetAtom(selectedWorkflowRunAtom);
    const mastraClient = useMastraClient();
    const workflow = mastraClient.getWorkflow(
        WorkflowId.POST_SESSION_WORKFLOW_ID,
    );
    const canUseWorkflows = adminUser.authorization.workflows.canUse;

    /* One request per membership, grouped by sessionId, instead of one per session row. */
    const { data: workflowRunsData, isSuccess: workflowRunsLoaded } =
        useGetWorkflowRuns({
            workflowId: WorkflowId.POST_SESSION_WORKFLOW_ID,
            resourceId: membershipDetails.guideId ?? undefined,
            membershipId: membershipDetails.id,
            enabled: canUseWorkflows,
        });
    const { mutateAsync: replayWorkflow, isPending: isReplayingWorkflow } =
        useReplayPostSessionWorkflow();

    const workflowRunsBySessionId = useMemo(() => {
        const runsBySessionId = new Map<string, WorkflowRun[]>();

        const allRuns = workflowRunsData?.runs ?? [];

        for (const run of allRuns) {
            const sessionId = getSessionIdFromWorkflowRun(run);
            if (sessionId == null) continue;

            const existing = runsBySessionId.get(sessionId);
            if (existing != null) {
                existing.push(run);
            } else {
                runsBySessionId.set(sessionId, [run]);
            }
        }

        return runsBySessionId;
    }, [workflowRunsData]);

    const [showUrls, setShowUrls] = useState(false);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedGuidanceSession, setSelectedGuidanceSession] =
        useState<GuidanceSession | null>(null);
    const [transcriptSession, setTranscriptSession] =
        useState<GuidanceSession | null>(null);
    const [selectedWorkflowRun, setSelectedWorkflowRun] =
        useState<WorkflowState | null>(null);
    const [isWorkflowRunModalOpen, setIsWorkflowRunModalOpen] = useState(false);

    const applySelectedWorkflowRun = useCallback(
        (run: WorkflowState) => {
            setSelectedWorkflowRun(run);
            setSelectedWorkflowRunAtom(run);
        },
        [setSelectedWorkflowRunAtom],
    );

    const refetchWorkflowRunLists = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: ['workflowRuns'],
        });
    }, [queryClient]);

    const handleReplayWorkflow = async (session: GuidanceSession) => {
        try {
            await replayWorkflow({ sessionId: session.id });
            toast.show('Workflow replay started', MutationSuccessToastOptions);
        } catch (err) {
            toast.show(
                'Could not replay the workflow',
                MutationErrorToastOptions(err),
            );
        }
    };

    const handleOpenWorkflowRun = async (run: WorkflowRun) => {
        try {
            const fullWorkflowRun = await workflow.runById(run.runId);
            applySelectedWorkflowRun(fullWorkflowRun);
            setIsWorkflowRunModalOpen(true);
        } catch (err) {
            toast.show(
                'Unable to fetch workflow details',
                MutationErrorToastOptions(err),
            );
            captureException(err);
        }
    };

    const canEditSession =
        adminUser.authorization.selectedMembership.guidanceSessions.canEdit;

    return (
        <YStack gap={12} width="100%">
            <XStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <H6Emphasized>All Sessions</H6Emphasized>
                <XStack alignItems="center" gap={16}>
                    {guidanceSessions.length > 0 && (
                        <CheckboxWithLabel
                            id="all-sessions-show-urls"
                            label="Show urls"
                            checked={showUrls}
                            onPress={() => {
                                setShowUrls((current) => !current);
                            }}
                        />
                    )}
                    {canEditSession && (
                        <Button
                            text="Add past session"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            onPress={() => {
                                setIsCreateModalOpen(true);
                            }}
                            width="auto"
                        />
                    )}
                </XStack>
            </XStack>
            {!guidanceSessions.length ? (
                <BodyMediumRegular fontStyle="italic">
                    No Guidance Sessions
                </BodyMediumRegular>
            ) : (
                <DataTable
                    value={guidanceSessions}
                    dataKey="id"
                    size="small"
                    tableStyle={{ width: '100%' }}
                    sortField="startTime"
                    sortOrder={-1}
                    scrollable
                    scrollHeight="70vh"
                >
                    <Column field="id" header="id" hidden={true} />
                    <Column
                        field="type"
                        header="Type"
                        body={({ type }: GuidanceSession) =>
                            getMembershipGuidanceSessionDisplayName(
                                type,
                                membershipDetails,
                            )
                        }
                    />
                    <Column
                        field="status"
                        header="Status"
                        sortable
                        body={(session: GuidanceSession): React.ReactNode =>
                            guidanceSessionStatusFromSession(session)
                        }
                    />
                    <Column
                        field="startTime"
                        header="Start Time"
                        sortable
                        body={({ startTime }: GuidanceSession) => {
                            return formatToSimpleDateTimeString(startTime, {
                                weekday: 'short',
                            });
                        }}
                    />
                    {showUrls && (
                        <Column
                            field="videoConferenceUrl"
                            header="Video Conference Url"
                            body={({ videoConferenceUrl }: GuidanceSession) => (
                                <UrlLinkRenderer
                                    linkText="Zoom"
                                    url={videoConferenceUrl}
                                />
                            )}
                        />
                    )}
                    {showUrls && (
                        <Column
                            field="calendlyRescheduleUrl"
                            header="Reschedule Url"
                            body={({
                                calendlyRescheduleUrl,
                            }: GuidanceSession) => (
                                <UrlLinkRenderer
                                    linkText="Reschedule"
                                    url={calendlyRescheduleUrl}
                                />
                            )}
                        />
                    )}
                    {showUrls && (
                        <Column
                            field="calendlyCancelUrl"
                            header="Cancel Url"
                            body={({ calendlyCancelUrl }: GuidanceSession) => (
                                <UrlLinkRenderer
                                    linkText="Cancel"
                                    url={calendlyCancelUrl}
                                />
                            )}
                        />
                    )}
                    <Column
                        header="Notes"
                        align="center"
                        body={(session: GuidanceSession) => (
                            <ViewItemRenderer
                                icon={ExternalLinkIcon}
                                onPress={() => {
                                    setIsNotesModalOpen(true);
                                    setSelectedGuidanceSession(session);
                                }}
                            />
                        )}
                    />
                    <Column
                        header="Transcript"
                        align="center"
                        body={(session: GuidanceSession) => {
                            if (session.transcriptS3Key == null) {
                                return null;
                            }

                            return (
                                <ViewItemRenderer
                                    icon={CaptionsIcon}
                                    onPress={() => {
                                        setTranscriptSession(session);
                                    }}
                                />
                            );
                        }}
                    />
                    {canUseWorkflows && (
                        <Column
                            header="Workflow"
                            align="center"
                            body={(session: GuidanceSession) => {
                                const runs =
                                    workflowRunsBySessionId.get(session.id) ??
                                    [];

                                return (
                                    <XStack
                                        gap={8}
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <WorkflowRunCell
                                            runs={runs}
                                            onOpenRun={handleOpenWorkflowRun}
                                        />
                                        {workflowRunsLoaded &&
                                            canReplayStoredTranscript(
                                                session.transcriptS3Key,
                                                runs,
                                            ) && (
                                                <Button
                                                    text="Replay"
                                                    variant={
                                                        ButtonVariant.TERTIARY
                                                    }
                                                    size={ButtonSize.TINY}
                                                    disabled={
                                                        isReplayingWorkflow
                                                    }
                                                    onPress={() => {
                                                        void handleReplayWorkflow(
                                                            session,
                                                        );
                                                    }}
                                                    width="auto"
                                                />
                                            )}
                                    </XStack>
                                );
                            }}
                        />
                    )}
                </DataTable>
            )}
            {isCreateModalOpen && (
                <CreateGuidanceSessionModal
                    isOpen={isCreateModalOpen}
                    membershipDetails={membershipDetails}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                    }}
                />
            )}
            {isNotesModalOpen && selectedGuidanceSession != null && (
                <GuidanceSessionNotesModal
                    membership={membershipDetails}
                    session={selectedGuidanceSession}
                    isOpen={isNotesModalOpen}
                    onClose={() => {
                        setSelectedGuidanceSession(null);
                        setIsNotesModalOpen(false);
                    }}
                />
            )}
            {transcriptSession != null && (
                <GuidanceSessionTranscriptModal
                    session={transcriptSession}
                    membershipDetails={membershipDetails}
                    isOpen
                    onClose={() => {
                        setTranscriptSession(null);
                    }}
                />
            )}
            {isWorkflowRunModalOpen && selectedWorkflowRun != null && (
                <PostSessionWorkflowRunOutputModal
                    isOpen={isWorkflowRunModalOpen}
                    workflowRun={selectedWorkflowRun}
                    onClose={() => {
                        setIsWorkflowRunModalOpen(false);
                        setSelectedWorkflowRun(null);
                        setSelectedWorkflowRunAtom(null);
                    }}
                    refetchWorkflowRuns={refetchWorkflowRunLists}
                />
            )}
        </YStack>
    );
};

const WorkflowRunCell = ({
    runs,
    onOpenRun,
}: {
    runs: WorkflowRun[];
    onOpenRun: (run: WorkflowRun) => Promise<void>;
}) => {
    if (runs.length === 0) return null;

    const runToOpen = pickWorkflowRunToOpen(runs);
    if (runToOpen == null) return null;

    const isComplete =
        normalizeWorkflowRunStatus(
            (runToOpen.snapshot as WorkflowRunState).status,
        ) === WorkflowStatus.SUCCESS;

    return (
        <IconButton
            variant={ButtonVariant.TERTIARY}
            tone={isComplete ? 'positive' : 'neutral'}
            icon={isComplete ? CheckIcon : LayersIcon}
            onPress={() => {
                void onOpenRun(runToOpen);
            }}
        />
    );
};

const pickWorkflowRunToOpen = (
    runs: WorkflowRun[],
): WorkflowRun | undefined => {
    const findByStatus = (target: WorkflowStatus) =>
        runs.find(
            (run) =>
                normalizeWorkflowRunStatus(
                    (run.snapshot as WorkflowRunState).status,
                ) === target,
        );

    return (
        findByStatus(WorkflowStatus.SUCCESS) ??
        findByStatus(WorkflowStatus.SUSPENDED) ??
        runs[0]
    );
};
